<template>
  <ContentPage variant="plain">
    <template #header>
      <div class="flex flex-col gap-2">
        <UiInput v-model="search" placeholder="Search name">
          <template #prepend>
            <IconSearchLine />
          </template>
        </UiInput>
        <Filter
          v-model="filterValues"
          storage-key="delegates"
          :fields="filterFields"
          :loading="isLoadingManagers || isLoadingMe"
        />
      </div>
    </template>
    <UiList
      :items="delegates"
      :loading="isListLoading"
      :finished="!hasNextPage"
      :empty-title="emptyTitle"
      :empty-description="emptyDescription"
      :on-refresh="refetch"
      @load-more="fetchNextPage"
    >
      <template #default="{ item }">
        <ParticipantCard
          :participant="item"
          show-check-ins
          @click="$router.push({ name: 'delegate', params: { delegate_id: item.id } })"
        />
      </template>
    </UiList>
  </ContentPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, shallowRef, watch } from 'vue'
import {
  useDelegatesInfiniteQuery,
  useMeQuery,
  usePersonalManagersQuery,
} from '@/queries/participants'
import { useSearchQuery } from '@/composables/search-query'
import { useEventTitle } from '@/composables/event-title'
import type { FilterOption, FilterSelectField } from '@/types/filter'

// Composables
const { search, debouncedSearch } = useSearchQuery()
useEventTitle()

// Refs
const filterValues = shallowRef<Record<string, string | string[]>>({})
const isReadyToQuery = shallowRef(false)

// Queries
const { data: managers, isLoading: isLoadingManagers } = usePersonalManagersQuery()
const {
  data: me,
  isLoading: isLoadingMe,
  isSuccess: isMeSuccess,
  isError: isMeError,
} = useMeQuery()

const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
  useDelegatesInfiniteQuery(
    computed(() => ({
      q: debouncedSearch.value || undefined,
      filter: {
        personal_manager_participant_id:
          filterValues.value.personal_manager !== 'all'
            ? (filterValues.value.personal_manager as string)
            : undefined,
      },
    })),
    { enabled: isReadyToQuery },
  )

// Computed
const delegates = computed(() => data.value?.pages.flatMap((p) => p.data) ?? [])
const isListLoading = computed(
  () =>
    isFetchingNextPage.value ||
    !isReadyToQuery.value ||
    (isFetching.value && !delegates.value.length),
)
const isFiltering = computed(
  () =>
    !!debouncedSearch.value ||
    (!!filterValues.value.personal_manager && filterValues.value.personal_manager !== 'all'),
)
const emptyTitle = computed(() => (isFiltering.value ? 'No Results Found' : 'No Delegates'))
const emptyDescription = computed(() =>
  isFiltering.value
    ? 'Try changing your search or filter options to find what you’re looking for'
    : 'Delegates appear here later',
)
const filterFields = computed<Array<FilterSelectField>>(() => {
  const options = (managers.value?.data ?? [])
    .filter((manager) => manager.id !== me.value?.id)
    .map((manager) => ({
      name: manager.name,
      value: manager.id,
      group: 'Managers',
    }))

  const quickOptions: Array<FilterOption> = []

  if (me.value) {
    quickOptions.push({
      name: 'My Companies',
      value: me.value.id,
    })
  }

  quickOptions.push({
    name: 'All',
    value: 'all',
  })

  return [
    {
      key: 'personal_manager',
      label: 'Personal Manager',
      type: 'select',
      default: me.value?.id ?? 'all',
      quickOptions,
      options,
    },
  ]
})

// onMounted: Filter уже смонтирован и filterValues заполнен корректными дефолтами.
// Если me уже загружен из кеша — сразу разрешаем запрос.
onMounted(() => {
  if (isMeSuccess.value || isMeError.value) {
    isReadyToQuery.value = true
  }
})

// После загрузки me: flush:'post' гарантирует что цепочка
// useFilter → Filter.vue → filterValues уже выполнилась.
watch(
  [isMeSuccess, isMeError],
  ([success, error]) => {
    if (success || error) {
      isReadyToQuery.value = true
    }
  },
  { flush: 'post' },
)

// Page
definePage({
  name: 'delegates',
  meta: {
    title: 'Delegates',
    disableBack: true,
  },
})
</script>

<style lang="sass" scoped>
@use '@/styles/variables'
</style>
